import { Box, Flex } from '@iheartjane/reefer';
import { load } from '@iheartjane/utils';
import { Suspense } from 'react';

import { useCustomerSelector } from '@jane/monolith/customer/redux';
import { selectIdentification } from '@jane/monolith/customer/redux/identification';
import { LoadingWrapper } from '@jane/shared/components';

import type { IdentificationProps } from './identification.types';
import { WarningMessage } from './warningMessage';

import styles from './mmjIdentification.module.css';

/** Code splitting to load convertIfPDF/pdfjs only when called */
const IdentificationUploader = load(
  () => import('./idUploader'),
  'IdentificationUploader'
);

const uploaderLabel = (customLanguage?: string | null, side?: string): string =>
  `Upload ${customLanguage || `${side} of medical ID card`}`;

export const MMJIdentification = ({
  showValidationErrors,
  valid,
  onIdUploadAttempt,
  onRemove,
  cardFrontComplianceLanguage,
  cardBackComplianceLanguage,
}: IdentificationProps) => {
  const { document, loading_mmj_card, loading_mmj_card_back } =
    useCustomerSelector(selectIdentification);

  return (
    <Suspense fallback={<LoadingWrapper isLoading />}>
      <Flex gap={24} className={styles['mmjIdentification']}>
        <IdentificationUploader
          document={document}
          label={uploaderLabel(cardFrontComplianceLanguage, 'front')}
          imageKey="mmj_card"
          inputName="mmj-card"
          isLoading={loading_mmj_card}
          valid={valid}
          showValidationErrors={showValidationErrors}
          onIdUploadAttempt={onIdUploadAttempt}
          onRemove={onRemove}
        />

        <IdentificationUploader
          document={document}
          imageKey="mmj_card_back"
          inputName="mmj-card-back"
          label={uploaderLabel(cardBackComplianceLanguage, 'back')}
          isLoading={loading_mmj_card_back}
          valid={valid}
          showValidationErrors={showValidationErrors}
          onIdUploadAttempt={onIdUploadAttempt}
          onRemove={onRemove}
        />
      </Flex>

      {(!document.mmj_card || !document.mmj_card_back) && (
        <Box mt={8} data-testid="mmj-id-warning">
          <WarningMessage>ID required for discount</WarningMessage>
        </Box>
      )}
    </Suspense>
  );
};
